/**
 * CSV Export Utilities
 * Export wallet tracker data to CSV format
 */

import type { TrackedWallet } from '../types/wallet.types';

/**
 * Convert data to CSV format
 */
function convertToCSV(data: any[], headers: string[]): string {
  const rows = [headers];

  data.forEach((item) => {
    const row = headers.map((header) => {
      const value = item[header] || '';
      // Escape commas and quotes
      const escaped = String(value).replace(/"/g, '""');
      return `"${escaped}"`;
    });
    rows.push(row);
  });

  return rows.map((row) => row.join(',')).join('\n');
}

/**
 * Trigger browser download
 */
function downloadCSV(csv: string, filename: string) {
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  const url = URL.createObjectURL(blob);

  link.setAttribute('href', url);
  link.setAttribute('download', filename);
  link.style.visibility = 'hidden';

  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);

  URL.revokeObjectURL(url);
}

/**
 * Export wallets to CSV
 */
export function exportWalletsToCSV(wallets: TrackedWallet[]) {
  const data = wallets.map((wallet) => ({
    label: wallet.label,
    address: wallet.address,
    ensName: wallet.ensName || '',
    chain: wallet.chain,
    tags: wallet.tags.join('; '),
    addedAt: new Date(wallet.addedAt).toLocaleDateString(),
    notes: wallet.notes || '',
  }));

  const headers = ['label', 'address', 'ensName', 'chain', 'tags', 'addedAt', 'notes'];
  const csv = convertToCSV(data, headers);

  const filename = `wallet-tracker-wallets-${new Date().toISOString().split('T')[0]}.csv`;
  downloadCSV(csv, filename);
}

/**
 * Export transactions to CSV
 */
export function exportTransactionsToCSV(transactions: any[], walletLabel: string) {
  const data = transactions.map((tx) => ({
    timestamp: tx.metadata?.blockTimestamp
      ? new Date(tx.metadata.blockTimestamp).toLocaleString()
      : '',
    hash: tx.hash || '',
    from: tx.from || '',
    to: tx.to || '',
    value: tx.value || '0',
    asset: tx.asset || 'ETH',
    category: tx.category || '',
    blockNumber: tx.blockNum || '',
  }));

  const headers = ['timestamp', 'hash', 'from', 'to', 'value', 'asset', 'category', 'blockNumber'];
  const csv = convertToCSV(data, headers);

  const filename = `${walletLabel.replace(/[^a-z0-9]/gi, '_')}-transactions-${
    new Date().toISOString().split('T')[0]
  }.csv`;
  downloadCSV(csv, filename);
}

/**
 * Export token holdings to CSV
 */
export function exportHoldingsToCSV(holdings: any[], walletLabel: string) {
  const data = holdings.map((holding) => ({
    symbol: holding.symbol || '',
    name: holding.name || '',
    balance: holding.balance || '0',
    contractAddress: holding.contractAddress || '',
    decimals: holding.decimals || '18',
  }));

  const headers = ['symbol', 'name', 'balance', 'contractAddress', 'decimals'];
  const csv = convertToCSV(data, headers);

  const filename = `${walletLabel.replace(/[^a-z0-9]/gi, '_')}-holdings-${
    new Date().toISOString().split('T')[0]
  }.csv`;
  downloadCSV(csv, filename);
}

/**
 * Export alerts to CSV
 */
export function exportAlertsToCSV(alerts: any[]) {
  const data = alerts.map((alert) => ({
    walletLabel: alert.walletLabel || '',
    walletAddress: alert.walletAddress,
    condition: alert.condition,
    valueThreshold: alert.valueThreshold || '',
    enabled: alert.enabled ? 'Yes' : 'No',
    channels: alert.channels.join('; '),
    triggerCount: alert.triggerCount,
    createdAt: new Date(alert.createdAt).toLocaleDateString(),
    lastTriggered: alert.lastTriggered
      ? new Date(alert.lastTriggered).toLocaleString()
      : 'Never',
  }));

  const headers = [
    'walletLabel',
    'walletAddress',
    'condition',
    'valueThreshold',
    'enabled',
    'channels',
    'triggerCount',
    'createdAt',
    'lastTriggered',
  ];
  const csv = convertToCSV(data, headers);

  const filename = `wallet-tracker-alerts-${new Date().toISOString().split('T')[0]}.csv`;
  downloadCSV(csv, filename);
}
